import React, { useState, useEffect } from "react";
import { useBuilding } from "../../context/BuildingContext";
import { reportsApi } from "../../services/api";
import Spinner from "../../components/common/Spinner";
import ErrorAlert from "../../components/common/ErrorAlert";

interface FinancialReportsProps {
  period: string;
}

const fmt = (n: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(n);

const fmtPct = (n: number) => `${n.toFixed(1)}%`;

const FinancialReports: React.FC<FinancialReportsProps> = ({ period }) => {
  const { currentBuilding } = useBuilding();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [report, setReport] = useState<any>(null);

  useEffect(() => {
    if (!currentBuilding?.id) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const data = await reportsApi.getProfitLoss(currentBuilding.id, period);
        if (!cancelled) setReport(data);
      } catch (err) {
        console.error("Error fetching P&L:", err);
        if (!cancelled)
          setError("Failed to load profit & loss report. Please try again.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [currentBuilding?.id, period]);

  if (loading) return <Spinner message="Loading income & expenditure…" />;
  if (error) return <ErrorAlert message={error} />;
  if (!report)
    return <ErrorAlert message="No data available for this period." />;

  const {
    startDate,
    endDate,
    totalIncome = 0,
    totalBills = 0,
    netIncome = 0,
    serviceChargesCollected = 0,
    serviceChargesByCategory = [],
    incomeByCategory = [],
    billsByCategory = [],
    monthlyBreakdown = [],
    budgetComparison,
  } = report;

  // Build a budget lookup keyed by category name
  const budgetByCategory = new Map<string, any>();
  if (budgetComparison) {
    for (const b of budgetComparison) budgetByCategory.set(b.category, b);
  }
  const totalBudgeted = budgetComparison
    ? budgetComparison.reduce((s: number, b: any) => s + (b.budgeted || 0), 0)
    : null;

  // Chart max scale — dynamic from the data
  const maxMonthly = Math.max(
    ...monthlyBreakdown.map((m: any) => Math.max(m.income || 0, m.bills || 0)),
    1,
  );

  return (
    <div>
      <h2 className="text-xl font-semibold text-gray-900 mb-1">
        Income & Expenditure
      </h2>
      <p className="text-sm text-gray-500 mb-6">
        {startDate} — {endDate}
      </p>

      {/* ── Summary Cards ── */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-8">
        <SummaryCard
          label="Service Charges Collected"
          value={fmt(serviceChargesCollected)}
          colour="text-blue-600"
        />
        <SummaryCard
          label="Other Income"
          value={fmt(totalIncome)}
          colour="text-green-600"
        />
        <SummaryCard
          label="Total Expenditure"
          value={fmt(totalBills)}
          colour="text-gray-900"
        />
        <SummaryCard
          label={netIncome >= 0 ? "Surplus" : "Deficit"}
          value={fmt(Math.abs(netIncome))}
          colour={netIncome >= 0 ? "text-green-600" : "text-red-600"}
        />
      </div>

      {/* ── Service Charges Breakdown ── */}
      {serviceChargesByCategory.length > 0 && (
        <CategoryTable
          title="Service Charges Collected"
          rows={serviceChargesByCategory}
          total={serviceChargesCollected}
          totalLabel="Total Service Charges"
        />
      )}

      {/* ── Income Breakdown ── */}
      {incomeByCategory.length > 0 && (
        <CategoryTable
          title="Other Income"
          rows={incomeByCategory}
          total={totalIncome}
          totalLabel="Total Other Income"
        />
      )}

      {/* ── Expenditure Breakdown (with optional budget columns) ── */}
      <div className="bg-white shadow rounded-lg mb-8">
        <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
          <h3 className="text-lg font-medium leading-6 text-gray-900">
            Expenditure by Category
          </h3>
        </div>
        <div className="px-4 py-5 sm:p-6 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <Th align="left">Category</Th>
                <Th>Actual</Th>
                {totalBudgeted !== null && <Th>Budget</Th>}
                {totalBudgeted !== null && <Th>Variance</Th>}
                <Th>% of Total</Th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {billsByCategory.map((row: any) => {
                const bud = budgetByCategory.get(row.category);
                return (
                  <tr key={row.category} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">
                      {row.category}
                    </td>
                    <Td>{fmt(row.amount)}</Td>
                    {totalBudgeted !== null && (
                      <Td>{bud ? fmt(bud.budgeted) : "—"}</Td>
                    )}
                    {totalBudgeted !== null && (
                      <Td
                        className={
                          bud
                            ? bud.variance >= 0
                              ? "text-green-600"
                              : "text-red-600"
                            : ""
                        }
                      >
                        {bud
                          ? `${fmt(Math.abs(bud.variance))} ${
                              bud.variance >= 0 ? "under" : "over"
                            }`
                          : "—"}
                      </Td>
                    )}
                    <Td>{fmtPct(row.percentOfTotal)}</Td>
                  </tr>
                );
              })}
            </tbody>
            <tfoot className="bg-gray-50 font-semibold">
              <tr>
                <td className="px-4 py-3 text-gray-900">Total Expenditure</td>
                <Td>{fmt(totalBills)}</Td>
                {totalBudgeted !== null && <Td>{fmt(totalBudgeted)}</Td>}
                {totalBudgeted !== null && (
                  <Td
                    className={
                      totalBudgeted - totalBills >= 0
                        ? "text-green-600"
                        : "text-red-600"
                    }
                  >
                    {fmt(Math.abs(totalBudgeted - totalBills))}{" "}
                    {totalBudgeted - totalBills >= 0 ? "under" : "over"}
                  </Td>
                )}
                <Td>100%</Td>
              </tr>
            </tfoot>
          </table>

          {/* Distribution bar chart */}
          {billsByCategory.length > 0 && (
            <div className="mt-8">
              <h4 className="text-sm font-medium text-gray-700 mb-3">
                Expenditure Distribution
              </h4>
              <div className="space-y-2">
                {billsByCategory.map((row: any) => (
                  <div key={row.category} className="flex items-center">
                    <div className="w-40 text-sm text-gray-600 truncate">
                      {row.category}
                    </div>
                    <div className="flex-1 bg-gray-200 rounded-full h-4">
                      <div
                        className="bg-primary-600 h-4 rounded-full"
                        style={{
                          width: `${Math.min(row.percentOfTotal, 100)}%`,
                        }}
                      />
                    </div>
                    <div className="w-16 text-sm text-gray-600 text-right ml-2">
                      {fmtPct(row.percentOfTotal)}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Monthly Breakdown Table ── */}
      {monthlyBreakdown.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Monthly Breakdown
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6 overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <Th align="left">Month</Th>
                  <Th>Income</Th>
                  <Th>Expenditure</Th>
                  <Th>Net</Th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {monthlyBreakdown.map((m: any) => (
                  <tr key={m.month} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-gray-900">{m.month}</td>
                    <Td>{fmt(m.income)}</Td>
                    <Td>{fmt(m.bills)}</Td>
                    <Td
                      className={
                        m.netIncome >= 0 ? "text-green-600" : "text-red-600"
                      }
                    >
                      {fmt(m.netIncome)}
                    </Td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ── Monthly Bar Chart ── */}
      {monthlyBreakdown.length > 0 && (
        <div className="bg-white shadow rounded-lg mb-8">
          <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
            <h3 className="text-lg font-medium leading-6 text-gray-900">
              Income vs Expenditure
            </h3>
          </div>
          <div className="px-4 py-5 sm:p-6">
            <div className="flex h-64 items-end space-x-1">
              {monthlyBreakdown.map((m: any) => {
                const incH = maxMonthly
                  ? ((m.income || 0) / maxMonthly) * 100
                  : 0;
                const expH = maxMonthly
                  ? ((m.bills || 0) / maxMonthly) * 100
                  : 0;
                return (
                  <div
                    key={m.month}
                    className="flex flex-col items-center flex-1 gap-0.5"
                  >
                    <div className="flex items-end w-full justify-center gap-px h-56">
                      <div
                        className="bg-green-500 rounded-t w-2/5"
                        style={{ height: `${incH}%` }}
                        title={`Income: ${fmt(m.income)}`}
                      />
                      <div
                        className="bg-red-400 rounded-t w-2/5"
                        style={{ height: `${expH}%` }}
                        title={`Expenditure: ${fmt(m.bills)}`}
                      />
                    </div>
                    <div className="text-xs text-gray-500 mt-1 truncate w-full text-center">
                      {m.month}
                    </div>
                  </div>
                );
              })}
            </div>
            <div className="mt-4 flex justify-center space-x-6 text-sm text-gray-600">
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 bg-green-500 rounded" />
                Income
              </span>
              <span className="flex items-center gap-1">
                <span className="inline-block w-3 h-3 bg-red-400 rounded" />
                Expenditure
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

/* ── small helper components ── */

const SummaryCard: React.FC<{
  label: string;
  value: string;
  colour: string;
}> = ({ label, value, colour }) => (
  <div className="bg-white overflow-hidden shadow rounded-lg">
    <div className="px-4 py-5 sm:p-6">
      <dt className="text-sm font-medium text-gray-500 truncate">{label}</dt>
      <dd className={`mt-1 text-3xl font-semibold ${colour}`}>{value}</dd>
    </div>
  </div>
);

const Th: React.FC<{
  align?: "left" | "right";
  children: React.ReactNode;
}> = ({ align = "right", children }) => (
  <th
    className={`px-4 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
      align === "left" ? "text-left" : "text-right"
    }`}
  >
    {children}
  </th>
);

const Td: React.FC<{
  className?: string;
  children: React.ReactNode;
}> = ({ className = "", children }) => (
  <td
    className={`px-4 py-3 whitespace-nowrap text-right tabular-nums ${className}`}
  >
    {children}
  </td>
);

const CategoryTable: React.FC<{
  title: string;
  rows: Array<{ category: string; amount: number; percentOfTotal: number }>;
  total: number;
  totalLabel: string;
}> = ({ title, rows, total, totalLabel }) => (
  <div className="bg-white shadow rounded-lg mb-8">
    <div className="px-4 py-5 border-b border-gray-200 sm:px-6">
      <h3 className="text-lg font-medium leading-6 text-gray-900">{title}</h3>
    </div>
    <div className="px-4 py-5 sm:p-6 overflow-x-auto">
      <table className="min-w-full divide-y divide-gray-200 text-sm">
        <thead className="bg-gray-50">
          <tr>
            <Th align="left">Category</Th>
            <Th>Amount</Th>
            <Th>% of Total</Th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r: any) => (
            <tr key={r.category} className="hover:bg-gray-50">
              <td className="px-4 py-3 font-medium text-gray-900">
                {r.category}
              </td>
              <Td>{fmt(r.amount)}</Td>
              <Td>{fmtPct(r.percentOfTotal)}</Td>
            </tr>
          ))}
        </tbody>
        <tfoot className="bg-gray-50 font-semibold">
          <tr>
            <td className="px-4 py-3 text-gray-900">{totalLabel}</td>
            <Td>{fmt(total)}</Td>
            <Td>100%</Td>
          </tr>
        </tfoot>
      </table>
    </div>
  </div>
);

export default FinancialReports;
